import React, { useEffect, useRef, useState } from "react";
import VideoForm from "./Form";
import { useParams } from "react-router-dom";
import { Box, Button, IconButton, Grid, useMediaQuery, 
  Dialog, DialogActions, DialogContent, DialogTitle, TextField,
  Tooltip, Alert, duration } from "@mui/material";
import YouTube from "react-youtube";
import BookmarkComponent from "./VideoBookmark";
import { useTheme } from "@emotion/react";
import { decodeBookmarks, getHeightByAspectRatio, getHeightByGridWidthAspectRatio, getWidthByBreakpoint, getYouTubeEmbedId } from "../utils/videoUtils";
import {useNavigate} from 'react-router-dom';
import { getAllSavedNotes, saveBookmarksInNotes } from "../../storage/LocalStorage";
import ExportBoard from "./ExportBoard";
import { exportBookmarks} from "../Export"
import { CTBox, StyledGrid } from "../style/StyledComponents";
import TimelineBar from "./TimelineBar";
import {TranscriptLoader, TranscriptViewer} from "./TranscriptViewer";
import { getSections, insertSection, StringToBookmarks } from "./bookmarks";
import ControlButtons from "./ControlButtons";
import { encode as base64_encode } from "base-64";
import QuickMarkBoard from "./QuickMarkBoard";

export const MAX_PLAYER_WIDTH = 1200;
export const MAX_PLAYER_WIDTH_XL = 1800;
export const URL_LENGTH_LIMIT = 2000;
const urlPrexif = "https://www.youtube.com/watch?v="
const aspectRatio = 16/9;
const initialBookmark = {
    type: "section", // functional type: section, timestamp
    time: 0,
    tags: [],
    text: "",
    color: "",
    notes: "",
    duration: 0,
}

const VideoPlayerPage = () => {

  // Form related state
  const [embedId, setEmbedId] = useState("");
  const [bookmark, setBookmark] = useState([{...initialBookmark}]);
  const [url, setUrl] = useState("");
  const [counter, setCounter] = useState(0);
  const [error, setError] = useState(true);
  const [isUrlChanged, setIsUrlChanged] = useState(false);
  
  const { id } = useParams();
  const { bk } = useParams();

  // Video related state
  const [player, setPlayer] = useState();
  const [loop, setLoop] = React.useState([]);
  const [loopButtonClick, setLoopButton] = React.useState(false);
  const [timeoutId, setTimeoutId] = React.useState();

  const theme = useTheme();
  const gridRef = useRef(null);

  const [fitWidth, setFitWidth] = useState(false);
  const [tabValue, setTabValue] = React.useState(0);
  const [openShareDialog, setOpenShareDialog] = useState(false);
  const [shareError, setShareError] = useState(false);

  const [timeStampsText, setTimeStampsText] = useState('');
  const [openQuickMarksDialog, setOpenQuickMarksDialog] = useState(false);
  
  const navigate = useNavigate();
  const bookmarkRef = useRef(bookmark);
  const embedIdRef = useRef(embedId);
  const playerRef = useRef(player);

  const [opts, setOpts] = useState({
    position: "absolute",
    top: 0,
    width: getWidthByBreakpoint(theme),
    height: getHeightByAspectRatio(getWidthByBreakpoint(theme)),
    playerVars: {
      autoplay: 1,
      loop: 1,
    },
  });
  // transcript state
  const [transcript, setTranscript] = useState([]);
  const [restElement, setRestElement] = useState(0);
  const [transcriptTimeoutId, setTranscriptTimeoutId] = useState();
  const [transcriptState, setTranscriptState] = useState("NOT_LOADED");
  // Component start code
  useEffect(() => {
    
    const handleBeforeUnload = (event) => {
      const embedId = embedIdRef.current;
      const bookmark = bookmarkRef.current;
      const player = playerRef.current;

      if (embedId && bookmark && player && player.getDuration() > 0) {
        const videoDetails = {
          title: player.videoTitle,
          updateTime: Date.now(),
          bookmark: bookmark
        };
        saveBookmarksInNotes(embedId, videoDetails);
      }
    };

    // Add event listener when component mounts
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Remove event listener when component unmounts
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // TODO sagar: document this ... id and bk is not ideal 
  useEffect(() => {
    const validatedEmbedId = getYouTubeEmbedId(urlPrexif + id);
    if (validatedEmbedId && bk) {
      setUrl(urlPrexif + id);
      setEmbedId(id);
      const dcod = decodeBookmarks(bk);
      if (dcod) {
        setBookmark(dcod.bookmarks);
        setTabValue(dcod.section ? dcod.section : 0);
      } else {
        setBookmark([{...initialBookmark}]);
        setTabValue(0);
      }
    } else if (validatedEmbedId) {
      const notes = getAllSavedNotes();
      setUrl(urlPrexif + id);
      setEmbedId(id);
      if (notes && notes[id]) {
        // For backwords compatibility :(
        const bookmarks = notes[id].bookmarks ? notes[id].bookmarks : notes[id].bookmark;
        setBookmark(bookmarks);
      } else {
        setBookmark([{...initialBookmark}]);
      }
    }

    return () => {
      const embedId = embedIdRef.current;
      const bookmark = bookmarkRef.current;
      const player = playerRef.current;

      if (embedId && bookmark && player && player.getDuration() > 0) {
        const videoDetails = {
          title: player.videoTitle,
          updateTime: Date.now(),
          bookmarks: bookmark.slice(0, -1)
        };
        
        saveBookmarksInNotes(embedId, videoDetails);
      }
    }
  }, [id, bk]);

  // TODO sagar: document this ... why?
  useEffect(() => {
    embedIdRef.current = embedId;
    playerRef.current = player;
    bookmarkRef.current = bookmark;
  }, [embedId, player, bookmark]);

  const handleStateChange = (event) => {
    const p = event;
    // Playing case: manage what needs to happen if you are in a loop
    if (event.target.getPlayerState() == 1 && loopButtonClick) {
      const duration = (loop[1] - player.getCurrentTime())/player.getPlaybackRate();
      const max_duration = (loop[1] - loop[0])/player.getPlaybackRate();
      if ( (duration >= 0) && (duration <= max_duration)){
        const localTimeoutId = setTimeout(restartVideoSection, duration * 1000);
        setTimeoutId(localTimeoutId);
      } else {
        if (timeoutId) {
          clearTimeout(timeoutId);
          setTimeoutId(null);
        }
        setLoopButton(false);
      }
    }
    // Pause case: remove loop timer
    if (event.target.getPlayerState() == 2 && loopButtonClick) {
      if (timeoutId) {
        clearTimeout(timeoutId);
        setTimeoutId(null);
      }
    }
  };

  const restartVideoSection = () => {
    player.seekTo(loop[0]);
    player.playVideo();
  }

  const handlePlaybackRateChange = (event) => {
    const p = event;
    if (event.target.getPlayerState() == 1 && loopButtonClick) {
      if (timeoutId) { // clear the existing timer
        clearTimeout(timeoutId);
        setTimeoutId(null);
      }
      // adjust the loop timer to take the play speed into account.
      const duration = (loop[1] - player.getCurrentTime())/player.getPlaybackRate();
      if (duration > 0){
        const localTimeoutId = setTimeout(restartVideoSection, duration * 1000);
        setTimeoutId(localTimeoutId);
      }
    }
  };

  const handleReadyEvent = (event) => {
    const bookmarkedTimes = bookmark ? bookmark.map((value) => value.time) : [];
    if (event.target) {
      const duration = Math.floor(event.target.getDuration());
      if (!bookmarkedTimes.includes(duration)) {
        // insert end section after the video loads
        insertSection(bookmark, setBookmark, duration)
      }
    }
    setPlayer(event.target);
  }

  const handleExportBookmarks = () => {
      exportBookmarks(embedId, player ? player.videoTitle : undefined, bookmark)
  };
    
  const handleSubmit = (e) => {
    e.preventDefault();
    const embedId = getYouTubeEmbedId(url);
    setEmbedId(embedId);
    setCounter(pre => pre + 1);
    setTranscript([]);
    setRestElement(0);
    clearTimeout(transcriptTimeoutId)
    navigate("/video" + "/" + embedId);
  }

  const handleTimelineClick = (sectionIndex) => {
    // const section = getSections(bookmark)
    // const index = i >= 0 ? i % (section.length - 1) : (i + section.length - 1) % (section.length - 1);
    // setTabValue(index);
    setTabValue(sectionIndex)
  }
  
  const handleTimelineDoubleClick = (sectionIndex) => {
    setTabValue(sectionIndex)
    if (timeoutId) { // Clear current timer, player's handleStateChange handle the remaining loop timer management
      clearTimeout(timeoutId);
      setTimeoutId(null);
    } 
    player.seekTo(getSections(bookmark)[sectionIndex].time);
    player.playVideo();
  }
  
  const handleBookmarkEvent = (e) => {
    e.preventDefault();
    const currentTime = Math.floor(player.getCurrentTime());
    const index = insertSection(
      bookmark, setBookmark,
      currentTime,
    )
    setTabValue(index)
  }
  
  const handleUnloopEvent = (event) => {
    setLoop([]);
    setLoopButton(false);
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }

  const handleStringToBookmarks = (event) => {
      StringToBookmarks(
        timeStampsText,
        bookmark,
        setBookmark
      )
  }

  const [openExportDialog, setOpenExportDialog] = useState(false);
  const handleOpenExportDialog = () => {
    setOpenExportDialog(true);
  };
  
  const [shareLink, setShareLink] = useState();
  // share link
  const handleShareButtonClick = () => {    
    const encodedBookmarks = base64_encode(
          JSON.stringify({
            bookmarks: bookmark.slice(0, -1),
          })
      )
      const shareLink = `${window.location.origin}/video/${embedId}/bookmark/${encodedBookmarks}`;
      setShareLink(shareLink);
  };

  const updateWidth = async () => {
    const gridWidth =  gridRef.current.offsetWidth;
    setOpts(opts => ({...opts, width: "100%", height: gridWidth / aspectRatio}));
  };

  const update = async () => {
    await updateWidth();
  }

  useEffect(() => {
    update(); // Get initial width

    window.addEventListener('resize', updateWidth); // Update width on resize
  
    return () => window.removeEventListener('resize', updateWidth); // Cleanup
  }, [gridRef, player]); // Empty dependency array means this effect runs once on mount 


  const handleStartClick = (i) => {
    if (timeoutId) { // Clear current timer, player's handleStateChange handle the remaining loop timer management
      clearTimeout(timeoutId);
      setTimeoutId(null);
    } 
    if (player) {
      player.seekTo(bookmark[i].time);
      player.playVideo();
    }
  }
  
  const handleLoopClick = (i) => {
    if (timeoutId) { // Clear current timer
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    setLoop([bookmark[i].time, bookmark[i + 1].time]);
    setLoopButton(true);
    handleStartClick(i);
  }

    return (
      <>
        <CTBox>
         <StyledGrid container ref={gridRef}>
            <VideoForm
              url={url}
              error={error}
              isUrlChanged={isUrlChanged}
              setError={setError}
              setUrl={setUrl}
              setIsUrlChanged={setIsUrlChanged}
              handleSubmit={handleSubmit}
              setBookmark={setBookmark}          
              setEmbedId={setEmbedId}
              setCounter={setCounter}
            />
         </StyledGrid>
          <Grid container spacing={2} sx={{paddingTop: 2}}>
           <Grid item xs={12} sm={12} md={12} lg={6} ref={gridRef} sx={{paddingRight: 1}}>
             <YouTube
                  videoId={embedId}
                  opts={opts}
                  onReady={(e) => {handleReadyEvent(e)}}
                  onStateChange={(e) => {handleStateChange(e)}}
                  onPlaybackRateChange={(e) => {handlePlaybackRateChange(e)}}
              />
              {player && bookmark && <TimelineBar 
                                      bookmark={bookmark}
                                      tabValue={tabValue}
                                      handleTimelineClick={handleTimelineClick} 
                                      handleTimelineDoubleClick={handleTimelineDoubleClick} />
              }
              {player && <ControlButtons
                          showShareUrl={true}
                          loop={loop}
                          loopButtonClick={loopButtonClick}
                          handleBookmarkEvent={handleBookmarkEvent}
                          handleUnloopEvent={handleUnloopEvent}
                          handleExportBookmarks={handleExportBookmarks}
                          handleOpenExportDialog={handleOpenExportDialog}
                          handleShareButtonClick={handleShareButtonClick}
                          shareLink={shareLink}
                          setOpenQuickMarksDialog={setOpenQuickMarksDialog}
                          />}
                <Grid item>
           {(transcriptState ==="NOT_LOADED") && 
                <TranscriptLoader 
                  embedId={embedId}
                  transcript={transcript}
                  setTranscript={setTranscript}
                  transcriptState={transcriptState}
                  setTranscriptState={setTranscriptState}
                />
           }
           {
            <Grid item paddingTop="1rem">
            {transcriptState == "NOT_FOUND" 
            && 
            <Alert severity="info" onClose={() => {setTranscriptState("NOT_LOADED"); setTranscript([]);}}
            >No transcript is found.</Alert>}
          </Grid>
           }
           { (transcript.length>0) &&
            <Grid item sx={{paddingTop: "1rem"}}>
              <TranscriptViewer 
                embedId={embedId}
                transcript={transcript} 
                restElement={restElement}
                setRestElement={setRestElement}
                player={player} 
                bookmark={bookmark}
                setBookmark={setBookmark}
                tabValue={tabValue}
                setTabValue={setTabValue}
                transcriptTimeoutId={transcriptTimeoutId}
                setTranscriptTimeoutId={setTranscriptTimeoutId}
                timeoutId={timeoutId}
                setTimeoutId={setTimeoutId}
                />
            </Grid> 
           }
           </Grid>
           </Grid>   
           {player && <Grid item xs={12} sm={12} md={12} lg={6} >
              <BookmarkComponent
                  embedId={embedId}
                  title={player.videoTitle ? player.videoTitle : ""}
                  bookmark={bookmark}
                  setBookmark={setBookmark}
                  gridRef={gridRef}
                  tabValue={tabValue}
                  setTabValue={setTabValue}
                  openShareDialog={openShareDialog}
                  setOpenShareDialog={setOpenShareDialog}
                  shareError={shareError}
                  setShareError={setShareError}
                  handleTimelineClick={handleTimelineClick}
                  handleTimelineDoubleClick={handleTimelineDoubleClick}
                  handleStartClick={handleStartClick}
                  handleLoopClick={handleLoopClick}
              />
          </Grid>
          }
        </Grid>
       <ExportBoard
          embedId={embedId}
          title={player ? player.videoTitle : "undefined"}
          openExportDialog={openExportDialog}
          setOpenExportDialog={setOpenExportDialog}
          bookmarks={bookmark} />
      <QuickMarkBoard 
        openQuickMarksDialog={openQuickMarksDialog}
        timeStampsText={timeStampsText}
        setTimeStampsText={setTimeStampsText}
        setOpenQuickMarksDialog={setOpenQuickMarksDialog}
        handleStringToBookmarks={handleStringToBookmarks}
      />
      </CTBox>
    </>
  );
}

export default VideoPlayerPage;